/**
 * The one panel per screen that answers the screen's question before the
 * table shows the evidence.
 *
 * It restates the figure the header already carries, large and with its
 * context: a status chip, a caveat, a trend, two or three facts. It never
 * introduces a number the screen does not otherwise have.
 *
 * The brand gradient appears exactly here, as a 3px rule on the top edge, and
 * nowhere else on a screen. That is what makes it read as the hero.
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { InfoHint } from "./info-hint";

export interface HeroFact {
  label: string;
  value: ReactNode;
  tone?: "default" | "bad";
  /** The derivation or caveat, shown as a tooltip on the label. */
  help?: string;
}

export function HeroPanel({
  eyebrow,
  figure,
  figureTone = "default",
  status,
  note,
  chart,
  facts,
  className,
}: {
  eyebrow: string;
  figure: ReactNode;
  figureTone?: "default" | "bad";
  /** A StatusChip, under the figure. */
  status?: ReactNode;
  /** How the figure is derived, or a caveat on it. Rides on the (i) beside the eyebrow. */
  note?: string;
  /** A TrendArea or HBars. Takes the middle column. */
  chart?: ReactNode;
  facts?: HeroFact[];
  className?: string;
}) {
  const hasFacts = Boolean(facts && facts.length > 0);
  const columns = chart && hasFacts ? 3 : chart || hasFacts ? 2 : 1;
  return (
    <section
      data-slot="hero-panel"
      data-columns={columns}
      className={cn(
        "relative grid items-center gap-7 overflow-hidden rounded-lg border bg-card px-6 py-[22px] text-card-foreground shadow-sm",
        columns === 3 && "lg:grid-cols-[auto_minmax(280px,1fr)_auto]",
        columns === 2 && "lg:grid-cols-[auto_minmax(280px,1fr)]",
        className,
      )}
    >
      <span
        data-hero-rule
        aria-hidden
        className="absolute inset-x-0 top-0 h-[3px]"
        style={{ background: "var(--hero-rule)" }}
      />
      <div className="min-w-0">
        <span className="mb-2 flex items-center gap-1.5 font-display text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {eyebrow}
          {note ? <InfoHint text={note} data-hero-note /> : null}
        </span>
        <div
          data-hero-figure
          data-tone={figureTone}
          className={cn(
            "font-display text-[44px] font-semibold leading-none tracking-tight tabular-nums",
            figureTone === "bad" && "text-destructive",
          )}
        >
          {figure}
        </div>
        {status ? <div className="mt-2.5">{status}</div> : null}
      </div>
      {chart ? <div className="min-w-0">{chart}</div> : null}
      {hasFacts ? (
        <dl className="flex max-w-md flex-wrap gap-x-8 gap-y-4">
          {facts?.map((fact) => (
            <div key={fact.label} className="min-w-0">
              <dt
                title={fact.help}
                className="mb-1 flex items-center gap-1 text-xs text-muted-foreground"
              >
                {fact.label}
                {fact.help ? (
                  <InfoHint text={fact.help} data-fact-help className="[&_svg]:size-3" />
                ) : null}
              </dt>
              <dd
                className={cn(
                  "font-display text-[22px] font-semibold leading-none tracking-tight tabular-nums",
                  fact.tone === "bad" && "text-destructive",
                )}
              >
                {fact.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </section>
  );
}
